import * as THREE from 'three'
import type { InteractionVisualStateContext } from '../../../manager/interactionState/AbstractInteractionVisualState'
import { isFeatureFace } from '../../../mesh/feature'
import type { FeatureFace } from '../../../mesh/feature'
import { resolveImportedModelTopologyRepository } from '../../topology/ImportedModelTopologyRepository'
import type { ImportedModelInstancedMeshUnit } from '../mesh-unit'
import { ImportedModelInstancedTopologyFaceOverlayMesh } from './ImportedModelInstancedTopologyFaceOverlayMesh'

/**
 * 把实例化 imported-model 的语义 FeatureFace 视觉投影到正式实例槽。
 *
 * 设计说明：
 * 1. 通用交互状态机继续负责计算 Hover/Selected 材质和维护 FeatureFace 语义状态；
 * 2. 本投影器在状态计算后隐藏实例语义面，并以单槽 InstancedMesh 输出同等视觉；
 * 3. 非实例 FeatureFace 不进入本类，继续走现有普通 Mesh 路径；
 * 4. Overlay、语义面可见性和 Unit 矩阵联动均由本类集中回收。
 */
export class ImportedModelInstancedTopologyFaceVisualProjector {
  /** 当前 FeatureFace UUID 到实例视觉覆盖对象的索引。 */
  private readonly overlayByFeatureFaceId = new Map<
    string,
    ImportedModelInstancedTopologyFaceOverlayMesh
  >()

  /** 当前活动 FeatureFace 到真实 source Mesh 的拓扑解析缓存。 */
  private readonly sourceMeshByFeatureFaceId = new Map<string, THREE.Mesh>()

  /** 当前被实例视觉替代的语义 FeatureFace。 */
  private readonly suppressedFeatureFaceById = new Map<string, FeatureFace>()

  /** 语义 FeatureFace 被替代前的可见性快照。 */
  private readonly originalVisibilityByFeatureFaceId = new Map<string, boolean>()

  /** 高频刷新期间复用的候选 FeatureFace 索引。 */
  private readonly candidateFeatureFaceById = new Map<string, FeatureFace>()

  /** 高频刷新期间复用的活动 FeatureFace UUID 集合。 */
  private readonly activeFeatureFaceIds = new Set<string>()

  /** 高频刷新期间复用的命令拓扑代理排除集合。 */
  private readonly commandTopologyProxyIds = new Set<string>()

  /**
   * 创建实例拓扑面视觉投影器。
   *
   * @param resolveInstancedMeshUnit 解析 source Mesh 当前正式实例 Unit 的 Viewer 级门面。
   */
  public constructor(
    private readonly resolveInstancedMeshUnit: (
      sourceMesh: THREE.Mesh,
    ) => ImportedModelInstancedMeshUnit | null,
  ) {}

  /**
   * 应用当前 Hover/Selected FeatureFace 的实例视觉投影。
   *
   * @param context 已经过当前模式过滤的视觉状态上下文。
   */
  public apply(context: InteractionVisualStateContext): void {
    this.commandTopologyProxyIds.clear()
    this.candidateFeatureFaceById.clear()
    this.activeFeatureFaceIds.clear()
    context.commandSelectedTopologyProxyObjects?.forEach((object) => {
      this.commandTopologyProxyIds.add(object.uuid)
    })
    context.selectedObjects.forEach((object) => {
      if (isFeatureFace(object) && !this.commandTopologyProxyIds.has(object.uuid)) {
        this.candidateFeatureFaceById.set(object.uuid, object)
      }
    })
    if (
      context.hoveredObject
      && isFeatureFace(context.hoveredObject)
      && !this.commandTopologyProxyIds.has(context.hoveredObject.uuid)
    ) {
      this.candidateFeatureFaceById.set(context.hoveredObject.uuid, context.hoveredObject)
    }

    this.candidateFeatureFaceById.forEach((featureFace) => {
      const pickHost = featureFace.getOwningResultMesh()
      if (!pickHost) {
        return
      }
      let sourceMesh = this.sourceMeshByFeatureFaceId.get(featureFace.uuid) ?? null
      if (!sourceMesh) {
        const repository = resolveImportedModelTopologyRepository(pickHost)
        sourceMesh = repository?.resolveVisibleMeshForPickHostFace(
          pickHost,
          featureFace.faceIndex,
        ) ?? null
      }
      if (!sourceMesh) {
        return
      }
      const unit = this.resolveInstancedMeshUnit(sourceMesh)
      if (!unit) {
        return
      }
      const overlayParent = unit.object.parent
      if (!overlayParent) {
        throw new Error(`实例拓扑面视觉投影缺少正式父级：${featureFace.uuid}`)
      }
      const material = Array.isArray(featureFace.material)
        ? featureFace.material[0] ?? null
        : featureFace.material
      if (!material) {
        return
      }

      const cachedOverlay = this.overlayByFeatureFaceId.get(featureFace.uuid)
      if (cachedOverlay) {
        cachedOverlay.syncFromSource(
          unit,
          sourceMesh,
          pickHost,
          featureFace,
          material,
        )
        if (cachedOverlay.parent !== overlayParent) {
          overlayParent.add(cachedOverlay)
        }
      } else {
        const overlay = new ImportedModelInstancedTopologyFaceOverlayMesh(
          unit,
          sourceMesh,
          pickHost,
          featureFace,
          material,
        )
        overlayParent.add(overlay)
        this.overlayByFeatureFaceId.set(featureFace.uuid, overlay)
      }
      this.sourceMeshByFeatureFaceId.set(featureFace.uuid, sourceMesh)
      this.suppressSemanticFeatureFace(featureFace)
      this.activeFeatureFaceIds.add(featureFace.uuid)
    })

    this.restoreUnusedSemanticFeatureFaces(this.activeFeatureFaceIds)
    this.disposeUnusedOverlays(this.activeFeatureFaceIds)
    this.candidateFeatureFaceById.clear()
    this.activeFeatureFaceIds.clear()
    this.commandTopologyProxyIds.clear()
  }

  /** 恢复全部语义面可见性并释放实例拓扑面覆盖对象。 */
  public reset(): void {
    this.suppressedFeatureFaceById.forEach((featureFace, featureFaceId) => {
      featureFace.visible = this.originalVisibilityByFeatureFaceId.get(featureFaceId) ?? true
    })
    this.suppressedFeatureFaceById.clear()
    this.originalVisibilityByFeatureFaceId.clear()
    this.overlayByFeatureFaceId.forEach((overlay) => {
      overlay.disposeOverlay()
    })
    this.overlayByFeatureFaceId.clear()
    this.sourceMeshByFeatureFaceId.clear()
    this.candidateFeatureFaceById.clear()
    this.activeFeatureFaceIds.clear()
    this.commandTopologyProxyIds.clear()
  }

  /** 结束投影器生命周期。 */
  public dispose(): void {
    this.reset()
  }

  /**
   * 隐藏已经由实例 Overlay 替代的语义 FeatureFace，并保存原始可见性。
   *
   * @param featureFace 当前语义面。
   */
  private suppressSemanticFeatureFace(featureFace: FeatureFace): void {
    if (!this.originalVisibilityByFeatureFaceId.has(featureFace.uuid)) {
      this.originalVisibilityByFeatureFaceId.set(featureFace.uuid, featureFace.visible)
    }
    this.suppressedFeatureFaceById.set(featureFace.uuid, featureFace)
    featureFace.visible = false
  }

  /**
   * 恢复本轮不再由实例 Overlay 表达的语义面可见性。
   *
   * @param activeFeatureFaceIds 本轮仍处于实例视觉状态的面 UUID。
   */
  private restoreUnusedSemanticFeatureFaces(activeFeatureFaceIds: ReadonlySet<string>): void {
    this.suppressedFeatureFaceById.forEach((featureFace, featureFaceId) => {
      if (activeFeatureFaceIds.has(featureFaceId)) {
        return
      }
      featureFace.visible = this.originalVisibilityByFeatureFaceId.get(featureFaceId) ?? true
      this.suppressedFeatureFaceById.delete(featureFaceId)
      this.originalVisibilityByFeatureFaceId.delete(featureFaceId)
    })
  }

  /**
   * 释放本轮不再需要的实例拓扑面覆盖对象。
   *
   * @param activeFeatureFaceIds 本轮需要保留的 FeatureFace UUID 集合。
   */
  private disposeUnusedOverlays(
    activeFeatureFaceIds: ReadonlySet<string>,
  ): void {
    this.overlayByFeatureFaceId.forEach((overlay, featureFaceId) => {
      if (!activeFeatureFaceIds.has(featureFaceId)) {
        overlay.disposeOverlay()
        this.overlayByFeatureFaceId.delete(featureFaceId)
        this.sourceMeshByFeatureFaceId.delete(featureFaceId)
      }
    })
  }
}
